import React, { useEffect, useState, useRef, useContext } from "react";
import { Row, Col, ProgressBar } from "react-bootstrap";
import { commonGetService, handleFilterApi } from "../../../utils/properties";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { showToast } from "../../../features/Toaster/toastslice";
import { activityContext, AccessInfo } from "../../../pages/Dashboard";
import moment from "moment";
import TopUsedAppsCard from "./TopUsedAppsCard";
import "../../../styles/Dashboard/topUsedApps.scss";


export interface WebApps {
    app: string;
    time: string;
    percentage: number;
    timeInSec: number
}

const TopUsedApps = () => {
    const [productiveLoading, setProductiveLoading] = useState<boolean>(false);
    const [unProductiveLoading, setUnProductiveLoading] = useState<boolean>(false);
    const [neutralLoading, setNeutralLoading] = useState<boolean>(false);
    const [productiveApps, setProductiveApps] = useState<WebApps[]>([]);
    const [totalProductiveTime, setTotalProductiveTime] = useState<string>('');
    const [unProductiveApps, setUnproductiveApps] = useState<WebApps[]>([]);
    const [totalUnproductiveTime, setTotalUnproductiveTime] = useState<string>('');
    const [neutralApps, setNeutralApps] = useState<WebApps[]>([]);
    const [totalNeutralTime, setTotalNeutralTime] = useState<string>('');

    //Details for Roles based UI
    const accessInfo: AccessInfo = useContext(activityContext)?.accessInfo as AccessInfo;

    const targetRef = useRef<HTMLDivElement>(null);
    const filterData = useAppSelector((state) => state.filterData);
    const dispatch = useAppDispatch();
    const {
        startDate,
        endDate,
        empDeviceId,
        filterType,
        organization,
        department,
    } = filterData; // Destructuring the filterData state value

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (accessInfo) {
                        if (accessInfo["Top Used Productive Apps"]) {
                            getProductiveWebApps();
                        }
                        if (accessInfo["Top Used Unproductive Apps"]) {
                            getUnproductiveWebApps();
                        }
                        if (accessInfo["Top Used Neutral Apps"]) {
                            getNeutralWebApps();
                        }
                    }
                    else {
                        getProductiveWebApps();
                        getUnproductiveWebApps();
                        getNeutralWebApps();
                    }
                    observer.disconnect();
                } else {
                }
            },
            {
                root: null,
                rootMargin: "0px",
                threshold: 0.25,
            }
        );

        if (targetRef.current) {
            observer.observe(targetRef.current);
        }

        // Cleanup function to disconnect the observer when the component unmounts
        return () => {
            observer.disconnect();
        };
    }, [startDate, endDate, empDeviceId, filterType, organization, department, accessInfo]);

    const getTotalHours = (totalSec: number): string => {
        let duration = moment.duration(totalSec, 'seconds');
        let hours = Math.floor(duration.asHours());
        let minutes = duration.minutes();

        return `${String(hours).padStart(2, '0')} hrs ${String(minutes).padStart(2, '0')} mins`
    }

    const getProductiveWebApps = () => {
        setProductiveLoading(true);
        commonGetService(
            `/api/1.0/dashboard/app/productive?${handleFilterApi(
                startDate,
                endDate,
                filterType,
                organization,
                empDeviceId,
                department,
                false
            )}`
        ).then((res) => {
            setProductiveLoading(false);
            if (res.status === 200) {
                let totalTimeInSec = 0;
                res?.data?.forEach((val: WebApps) => {
                    totalTimeInSec += val?.timeInSec;
                })
                setTotalProductiveTime(getTotalHours(totalTimeInSec));
                setProductiveApps(res?.data);
            }
            else {
                dispatch(
                    showToast({
                        message: "Something went wrong in API.",
                        status: "retry",
                        visible: true,
                    })
                );
            }
        });
    };

    const getUnproductiveWebApps = () => {
        setUnProductiveLoading(true);
        commonGetService(
            `/api/1.0/dashboard/app/unproductive?${handleFilterApi(
                startDate,
                endDate,
                filterType,
                organization,
                empDeviceId,
                department,
                false
            )}`
        ).then((res) => {
            setUnProductiveLoading(false);
            if (res.status === 200) {
                let totalTimeInSec = 0;
                res?.data?.forEach((val: WebApps) => {
                    totalTimeInSec += val?.timeInSec;
                })
                setTotalUnproductiveTime(getTotalHours(totalTimeInSec));
                setUnproductiveApps(res?.data);
            }
            else {
                dispatch(
                    showToast({
                        message: "Something went wrong in API.",
                        status: "retry",
                        visible: true,
                    })
                );
            }
        });
    };

    const getNeutralWebApps = () => {
        setNeutralLoading(true);
        commonGetService(
            `/api/1.0/dashboard/app/neutral?${handleFilterApi(
                startDate,
                endDate,
                filterType,
                organization,
                empDeviceId,
                department,
                false
            )}`
        ).then((res) => {
            setNeutralLoading(false);
            if (res.status === 200) {
                let totalTimeInSec = 0;
                res?.data?.forEach((val: WebApps) => {
                    totalTimeInSec += val?.timeInSec;
                })
                setTotalNeutralTime(getTotalHours(totalTimeInSec));
                setNeutralApps(res?.data);
            }
            else {
                dispatch(
                    showToast({
                        message: "Something went wrong in API.",
                        status: "retry",
                        visible: true,
                    })
                );
            }
        });
    };

    const isThreeCard = accessInfo ? accessInfo["Top Used Productive Apps"] && accessInfo["Top Used Unproductive Apps"] && accessInfo["Top Used Neutral Apps"] : true;
    const isSingleCard = accessInfo ? (accessInfo["Top Used Productive Apps"] && !accessInfo["Top Used Unproductive Apps"] && !accessInfo["Top Used Neutral Apps"]) ||
        (!accessInfo["Top Used Productive Apps"] && accessInfo["Top Used Unproductive Apps"] && !accessInfo["Top Used Neutral Apps"]) ||
        (!accessInfo["Top Used Productive Apps"] && !accessInfo["Top Used Unproductive Apps"] && accessInfo["Top Used Neutral Apps"]) : false;
    const isCustomStyle = isThreeCard || isSingleCard;

    return (
        <div className="app-container" ref={targetRef}>
            <Row className="app-row">
                {accessInfo ?
                    <>
                        {accessInfo["Top Used Productive Apps"] &&
                            <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                                <TopUsedAppsCard
                                    isLoading={productiveLoading}
                                    totalTime={totalProductiveTime}
                                    appData={productiveApps}
                                    title="Most used productive apps & sites"
                                />
                            </Col>
                        }
                        {accessInfo["Top Used Unproductive Apps"] &&
                            <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                                <TopUsedAppsCard
                                    isLoading={unProductiveLoading}
                                    totalTime={totalUnproductiveTime}
                                    appData={unProductiveApps}
                                    title="Most used unproductive apps & sites"
                                />
                            </Col>
                        }
                        {accessInfo["Top Used Neutral Apps"] &&
                            <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                                <TopUsedAppsCard
                                    isLoading={neutralLoading}
                                    totalTime={totalNeutralTime}
                                    appData={neutralApps}
                                    title="Most used neutral apps & sites"
                                />
                            </Col>
                        }
                    </>
                    :
                    <>
                        <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                            <TopUsedAppsCard
                                isLoading={productiveLoading}
                                totalTime={totalProductiveTime}
                                appData={productiveApps}
                                title="Most used productive apps & sites"
                            />
                        </Col>
                        <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                            <TopUsedAppsCard
                                isLoading={unProductiveLoading}
                                totalTime={totalUnproductiveTime}
                                appData={unProductiveApps}
                                title="Most used unproductive apps & sites"
                            />
                        </Col>
                        <Col className={`app-col ${isCustomStyle ? 'custom-col' : ''}`}>
                            <TopUsedAppsCard
                                isLoading={neutralLoading}
                                totalTime={totalNeutralTime}
                                appData={neutralApps}
                                title="Most used neutral apps & sites"
                            />
                        </Col>
                    </>
                }
            </Row>
        </div>
    );
};

export default TopUsedApps;